import { Form, Message, Segment } from '@mairistem/composants';
import { newIdentifiant } from '@mairistem/tools';
import _ from 'lodash';
import React from 'react';
import { useHistory } from 'react-router';

import ModalEncaisseDepassee from '@/components/Encaissement/ModalEncaissement/ModalEncaisseDepassee';
import { ModalEncaissementComptant } from '@/components/Encaissement/ModalEncaissementComptant';
import { useEncaissement } from '@/hooks';
import { baseUrlFront } from '@/routes';
import type { PayeurType, RecuEncaissementType } from '@/types';
import type { ProduitsListeItemType } from '@/types/produit';
import {
  annulerRetourComptant,
  armerRetourComptant,
  consommerPayeurCree,
} from '@/utils/comptantHandoff';
import ComptantActions from './ComptantActions';
import ComptantSaisie from './ComptantSaisie';
import ComptantTable from './ComptantTable';
import { clampQuantite, type LigneComptant, QUANTITE_MIN } from './comptant';

import './ComptantPanel.less';

interface ComptantPanelProps {
  envoiRecuParMail?: boolean;
  onRecuCree?: (recu: RecuEncaissementType) => void;
}

const ComptantPanel = ({ envoiRecuParMail = false, onRecuCree }: ComptantPanelProps) => {
  const history = useHistory();

  const [payeur, setPayeur] = React.useState<PayeurType | null>(null);
  const [produit, setProduit] = React.useState<ProduitsListeItemType | null>(null);
  // `null` = pas encore de quantité : le champ reste vide jusqu'à la sélection d'un produit.
  const [quantite, setQuantite] = React.useState<number | null>(null);
  const [lignes, setLignes] = React.useState<LigneComptant[]>([]);
  // État indiquant si la modale d'encaissement au comptant est ouverte
  const [modalEncaissement, setModalEncaissement] = React.useState<boolean>(false);

  const { saveEncaissementComptant, encaisseDepassee, resetEncaisseDepassee } = useEncaissement();

  const handleCreatePayeur = React.useCallback(() => {
    // On « arme » le retour puis on ouvre la fiche payeur en création.
    armerRetourComptant();
    history.push(`${baseUrlFront}/payeurs/${newIdentifiant()}`);
  }, [history]);

  // Au retour de la création d'un payeur, on le pré-sélectionne. On nettoie aussi
  // tout retour resté « armé » (on est sur l'encaissement : l'intention est caduque).
  React.useEffect(() => {
    annulerRetourComptant();
    const payeurCree = consommerPayeurCree();
    if (payeurCree) {
      setPayeur(payeurCree);
    }
  }, []);

  /**
   * La quantité n'a de sens qu'une fois le produit choisi : elle se renseigne à 1 à la
   * sélection (sauf si une quantité a déjà été saisie) et redevient vide sans produit.
   */
  const handleProduitChange = React.useCallback((nouveauProduit: ProduitsListeItemType | null) => {
    setProduit(nouveauProduit);
    setQuantite((prev) => {
      if (_.isNil(nouveauProduit)) return null;
      return _.isNil(prev) ? QUANTITE_MIN : prev;
    });
  }, []);

  /**
   * Ajout d'un produit déjà présent dans la table : on incrémente la quantité de sa ligne au
   * lieu d'en créer une seconde pour le même produit. Le cumul reste borné par QUANTITE_MAX.
   */
  const handleValider = React.useCallback(() => {
    if (_.isNil(produit)) return;

    const ajout = clampQuantite(quantite ?? QUANTITE_MIN);

    setLignes((prev) => {
      const existante = _.find(prev, (l) => l.produit.identifiant === produit.identifiant);

      if (existante) {
        return _.map(prev, (l) =>
          l.key === existante.key ? { ...l, quantite: clampQuantite(l.quantite + ajout) } : l,
        );
      }

      return [...prev, { key: newIdentifiant(), produit, quantite: ajout }];
    });

    setProduit(null);
    setQuantite(null);
  }, [produit, quantite]);

  const handleChangeQuantiteLigne = React.useCallback((key: string, value: string) => {
    const parsed = clampQuantite(Number(value));
    setLignes((prev) => _.map(prev, (l) => (l.key === key ? { ...l, quantite: parsed } : l)));
  }, []);

  // Supprimer une ligne produit n'a aucun impact sur le payeur sélectionné (cf. ticket).
  const handleRemoveLigne = React.useCallback((key: string) => {
    setLignes((prev) => _.filter(prev, (l) => l.key !== key));
  }, []);

  const handleReinitialiser = React.useCallback(() => {
    setPayeur(null);
    setProduit(null);
    setQuantite(null);
    setLignes([]);
  }, []);

  const total = React.useMemo(
    () => _.sumBy(lignes, (l) => l.quantite * l.produit.prixUnitaireTTC),
    [lignes],
  );

  /**
   * Encaissement au comptant : l'API crée la facture, ses lignes et le reçu en une seule
   * transaction. En cas de succès, la saisie est entièrement réinitialisée pour enchaîner
   * sur l'encaissement suivant. Les erreurs remontent à la modale, qui affiche le toast.
   */
  const handleSaveEncaissement = React.useCallback(
    async (body: Record<string, unknown>, imprimerRecu = false, envoyerMail = false) => {
      const recu = await saveEncaissementComptant(body, imprimerRecu, envoyerMail);

      if (recu) {
        setModalEncaissement(false);
        handleReinitialiser();
        onRecuCree?.(recu);
      }
    },
    [handleReinitialiser, onRecuCree, saveEncaissementComptant],
  );

  const isEmpty = _.isNil(payeur) && lignes.length === 0;
  const hasSaisie = !_.isNil(payeur) || !_.isNil(produit) || lignes.length > 0;

  return (
    <Segment basic padded>
      <Form className="comptant-panel">
        <ComptantSaisie
          payeur={payeur}
          produit={produit}
          quantite={quantite}
          onPayeurChange={setPayeur}
          onCreatePayeur={handleCreatePayeur}
          onProduitChange={handleProduitChange}
          onQuantiteChange={setQuantite}
          onValider={handleValider}
        />

        {isEmpty ? (
          <Message
            info
            style={{ marginLeft: 0, marginTop: '2em' }}
            data-testid="comptant-message-payeur"
            header="Sélectionner un payeur pour lequel faire un encaissement"
          />
        ) : (
          <ComptantTable
            payeur={payeur}
            lignes={lignes}
            total={total}
            onRemovePayeur={() => setPayeur(null)}
            onRemoveLigne={handleRemoveLigne}
            onChangeQuantiteLigne={handleChangeQuantiteLigne}
          />
        )}

        {hasSaisie && (
          <ComptantActions
            encaisserDisabled={_.isNil(payeur) || lignes.length === 0}
            onReinitialiser={handleReinitialiser}
            onEncaisser={() => setModalEncaissement(true)}
          />
        )}
      </Form>

      {modalEncaissement && !_.isNil(payeur) && (
        <ModalEncaissementComptant
          payeur={payeur}
          lignes={lignes}
          total={total}
          envoiRecuParMail={envoiRecuParMail}
          onSave={handleSaveEncaissement}
          onClose={() => setModalEncaissement(false)}
        />
      )}

      {encaisseDepassee && (
        <ModalEncaisseDepassee
          encaisseGlobaleDepassee={encaisseDepassee.encaisseGlobaleDepassee}
          encaisseNumeraireDepassee={encaisseDepassee.encaisseNumeraireDepassee}
          onClose={resetEncaisseDepassee}
        />
      )}
    </Segment>
  );
};

export default ComptantPanel;
